/**
 * McDuffy Premium Animations & UX JavaScript
 * Mobile-first, performance-optimized
 */

(function() {
  'use strict';

  // ==========================================================================
  // 1. SCROLL ANIMATIONS (Intersection Observer)
  // ==========================================================================

  const initScrollAnimations = () => {
    const animatedElements = document.querySelectorAll('.animate-on-scroll, [data-animate]');

    if (!animatedElements.length) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      animatedElements.forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -10% 0px', // Trigger slightly before element is fully visible
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Add stagger delay if specified
          const staggerIndex = entry.target.dataset.stagger;
          if (staggerIndex) {
            entry.target.style.animationDelay = `${staggerIndex * 0.1}s`;
          }

          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target); // Only animate once
        }
      });
    }, observerOptions);

    animatedElements.forEach(el => observer.observe(el));
  };

  // ==========================================================================
  // 2. CART BADGE ANIMATION
  // ==========================================================================

  const initCartBadgeAnimation = () => {
    const cartBadges = document.querySelectorAll('.header__cart-count, .cart-count, [data-cart-count]');
    let previousCount = 0;

    // Listen for cart updates
    document.addEventListener('cart:updated', (e) => {
      const newCount = e.detail?.count || 0;

      if (newCount > previousCount) {
        cartBadges.forEach(badge => {
          badge.classList.remove('cart-badge-pop');
          void badge.offsetWidth; // Force reflow
          badge.classList.add('cart-badge-pop');
        });
      }

      previousCount = newCount;
    });

    // Also listen for Shopify's native cart events
    if (window.Shopify && window.Shopify.onCartUpdate) {
      window.Shopify.onCartUpdate = (cart) => {
        const newCount = cart.item_count || 0;
        if (newCount > previousCount) {
          cartBadges.forEach(badge => {
            badge.classList.remove('cart-badge-pop');
            void badge.offsetWidth;
            badge.classList.add('cart-badge-pop');
          });
        }
        previousCount = newCount;
      };
    }
  };

  // ==========================================================================
  // 3. SWIPE GESTURES FOR MOBILE DRAWERS
  // ==========================================================================

  const initSwipeGestures = () => {
    const drawers = document.querySelectorAll('.drawer, [data-drawer], cart-drawer');

    drawers.forEach(drawer => {
      let startX = 0;
      let startY = 0;
      let isDragging = false;

      drawer.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = true;
      }, { passive: true });

      drawer.addEventListener('touchmove', (e) => {
        if (!isDragging) return;

        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const diffX = currentX - startX;
        const diffY = Math.abs(currentY - startY);

        // Only trigger if horizontal swipe is dominant
        if (diffX > 50 && diffY < 50) {
          // Swipe right to close (drawer opens from right)
          const closeBtn = drawer.querySelector('.drawer__close, [data-drawer-close], .drawer__close-btn');
          if (closeBtn) {
            closeBtn.click();
          }
          isDragging = false;
        }
      }, { passive: true });

      drawer.addEventListener('touchend', () => {
        isDragging = false;
      }, { passive: true });
    });
  };

  // ==========================================================================
  // 4. BUTTON RIPPLE EFFECT
  // ==========================================================================

  const initButtonRipple = () => {
    const buttons = document.querySelectorAll('.btn-ripple, .btn--primary');

    buttons.forEach(button => {
      button.addEventListener('click', function(e) {
        const rect = this.getBoundingClientRect();
        const ripple = document.createElement('span');
        const size = Math.max(rect.width, rect.height);

        ripple.style.cssText = `
          position: absolute;
          width: ${size}px;
          height: ${size}px;
          left: ${e.clientX - rect.left - size / 2}px;
          top: ${e.clientY - rect.top - size / 2}px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          transform: scale(0);
          animation: ripple 0.4s ease-out;
          pointer-events: none;
        `;

        this.style.position = 'relative';
        this.style.overflow = 'hidden';
        this.appendChild(ripple);

        setTimeout(() => ripple.remove(), 400);
      });
    });
  };

  // ==========================================================================
  // 5. INPUT FOCUS ANIMATIONS
  // ==========================================================================

  const initInputAnimations = () => {
    const inputs = document.querySelectorAll('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea, select');

    inputs.forEach(input => {
      input.classList.add('input-focus-glow');

      // Floating label support
      const wrapper = input.closest('.floating-label-group');
      if (wrapper) {
        input.addEventListener('focus', () => {
          wrapper.classList.add('is-focused');
        });

        input.addEventListener('blur', () => {
          wrapper.classList.remove('is-focused');
          if (input.value) {
            wrapper.classList.add('has-value');
          } else {
            wrapper.classList.remove('has-value');
          }
        });
      }
    });
  };

  // ==========================================================================
  // 6. FORM VALIDATION SHAKE
  // ==========================================================================

  const initFormValidation = () => {
    document.addEventListener('submit', (e) => {
      const form = e.target;
      const invalidInputs = form.querySelectorAll(':invalid');

      invalidInputs.forEach(input => {
        input.classList.add('shake');
        input.addEventListener('animationend', () => {
          input.classList.remove('shake');
        }, { once: true });
      });
    });
  };

  // ==========================================================================
  // 7. CART ITEM ANIMATIONS
  // ==========================================================================

  const initCartAnimations = () => {
    // Listen for item removal
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.cart-item__remove, [data-remove-item]');
      if (!removeBtn) return;

      const cartItem = removeBtn.closest('.cart-item, [data-cart-item]');
      if (cartItem) {
        cartItem.classList.add('cart-item-remove');
      }
    });

    // Quantity update animation
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-quantity-input], .quantity-input')) {
        const wrapper = e.target.closest('.cart-item, [data-cart-item]');
        if (wrapper) {
          wrapper.classList.add('quantity-updated');
          setTimeout(() => wrapper.classList.remove('quantity-updated'), 500);
        }
      }
    });
  };

  // ==========================================================================
  // 8. BOTTOM NAV ACTIVE STATE
  // ==========================================================================

  const initBottomNav = () => {
    const bottomNavItems = document.querySelectorAll('.mcduffy-bottom-nav__item, [data-bottom-nav-item]');
    const currentPath = window.location.pathname;

    bottomNavItems.forEach(item => {
      const href = item.getAttribute('href') || item.dataset.href;

      if (href && (currentPath === href || currentPath.startsWith(href + '/'))) {
        item.classList.add('is-active');
      }

      // Add tap feedback
      item.addEventListener('touchstart', function() {
        this.classList.add('is-tapped');
      }, { passive: true });

      item.addEventListener('touchend', function() {
        setTimeout(() => this.classList.remove('is-tapped'), 150);
      }, { passive: true });
    });
  };

  // ==========================================================================
  // 9. TOAST NOTIFICATIONS
  // ==========================================================================

  window.showToast = (message, type = 'info', duration = 3000) => {
    const toast = document.createElement('div');
    toast.className = `toast toast--${type} toast-enter`;
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      padding: 12px 24px;
      background: ${type === 'success' ? '#193613' : type === 'error' ? '#DC2626' : '#1A1A1A'};
      color: white;
      border-radius: 8px;
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `;

    // Adjust for bottom nav on mobile
    if (window.innerWidth <= 768) {
      toast.style.bottom = 'calc(80px + env(safe-area-inset-bottom, 0px))';
    }

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.remove('toast-enter');
      toast.classList.add('toast-exit');
      setTimeout(() => toast.remove(), 300);
    }, duration);

    return toast;
  };

  // ==========================================================================
  // 10. PRODUCT CARD HOVER (Desktop) / TAP (Mobile)
  // ==========================================================================

  const initProductCards = () => {
    const cards = document.querySelectorAll('.product-card, .card, .card--product');

    cards.forEach(card => {
      // Add hover classes
      card.classList.add('card-hover-lift');

      // Image zoom
      const image = card.querySelector('.product-card__image, .card__image');
      if (image) {
        image.classList.add('image-hover-zoom');
      }
    });
  };

  // ==========================================================================
  // 11. LAZY LOADING ENHANCEMENT
  // ==========================================================================

  const initLazyLoading = () => {
    // Native lazy loading is already supported
    // Do not hide images before native lazy loading starts. Several homepage
    // proof sections live in horizontal/offscreen layouts, and forcing inline
    // opacity: 0 can leave already-visible images blank until a late load event.
    const images = document.querySelectorAll('img[loading="lazy"]');

    images.forEach(img => {
      img.style.transition = 'opacity 0.3s ease';

      img.addEventListener('load', () => {
        img.style.opacity = '1';
      });

      img.addEventListener('error', () => {
        img.style.opacity = '1';
      });

      // Handle already loaded images
      if (img.complete) {
        img.style.opacity = '1';
      }
    });
  };

  // ==========================================================================
  // 12. SMOOTH SCROLL FOR ANCHOR LINKS
  // ==========================================================================

  const initSmoothScroll = () => {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;

        const target = document.querySelector(targetId);
        if (!target) return;

        e.preventDefault();

        // Account for fixed header
        const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
        const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      });
    });
  };

  // ==========================================================================
  // 13. MOBILE PULL-TO-CLOSE DRAWER
  // ==========================================================================

  const initPullToClose = () => {
    const drawers = document.querySelectorAll('cart-drawer, .drawer, [data-drawer]');

    drawers.forEach(drawer => {
      let startY = 0;
      let currentY = 0;
      let isDragging = false;

      const header = drawer.querySelector('.drawer__header');
      if (!header) return;

      header.addEventListener('touchstart', (e) => {
        startY = e.touches[0].clientY;
        isDragging = true;
      }, { passive: true });

      header.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        currentY = e.touches[0].clientY;
        const diffY = currentY - startY;

        // Pull down to close
        if (diffY > 80) {
          const closeBtn = drawer.querySelector('.drawer__close, [data-drawer-close], .drawer__close-btn');
          if (closeBtn) {
            closeBtn.click();
          }
          isDragging = false;
        }
      }, { passive: true });

      header.addEventListener('touchend', () => {
        isDragging = false;
      }, { passive: true });
    });
  };

  // ==========================================================================
  // 14. IMAGE CAROUSEL SWIPE DETECTION
  // ==========================================================================

  const initCarouselSwipe = () => {
    const carousels = document.querySelectorAll('.product__media-list, .slider, [data-slider]');

    carousels.forEach(carousel => {
      // Check if can scroll and add visual indicator
      const checkScrollable = () => {
        if (carousel.scrollWidth > carousel.clientWidth) {
          carousel.classList.add('can-scroll');
        } else {
          carousel.classList.remove('can-scroll');
        }
      };

      checkScrollable();
      window.addEventListener('resize', checkScrollable);

      // Scroll indicator dots
      let scrollTimeout;
      carousel.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        carousel.classList.add('is-scrolling');

        scrollTimeout = setTimeout(() => {
          carousel.classList.remove('is-scrolling');
        }, 150);
      }, { passive: true });
    });
  };

  // ==========================================================================
  // 15. LOADING STATE MANAGEMENT
  // ==========================================================================

  const initLoadingStates = () => {
    // Add loading state to buttons on form submit
    document.addEventListener('submit', (e) => {
      const form = e.target;
      const submitBtn = form.querySelector('[type="submit"], .btn--primary');

      if (submitBtn && !submitBtn.classList.contains('is-loading')) {
        submitBtn.classList.add('is-loading');
        submitBtn.setAttribute('aria-busy', 'true');

        // Remove loading after timeout (fallback)
        setTimeout(() => {
          submitBtn.classList.remove('is-loading');
          submitBtn.removeAttribute('aria-busy');
        }, 10000);
      }
    });

    // Add loading to add-to-cart buttons
    document.addEventListener('click', (e) => {
      const atcBtn = e.target.closest('[data-add-to-cart], .product-form__submit, .add-to-cart');
      if (!atcBtn) return;

      atcBtn.classList.add('is-loading');
      atcBtn.setAttribute('aria-busy', 'true');

      // Listen for cart update to remove loading
      const removeLoading = () => {
        atcBtn.classList.remove('is-loading');
        atcBtn.removeAttribute('aria-busy');
        document.removeEventListener('cart:updated', removeLoading);
      };

      document.addEventListener('cart:updated', removeLoading);

      // Fallback timeout
      setTimeout(removeLoading, 5000);
    });
  };

  // ==========================================================================
  // 16. BOTTOM NAV CART BADGE SYNC
  // ==========================================================================

  const initBottomNavCartSync = () => {
    const updateBottomNavBadge = (count) => {
      const badge = document.querySelector('.mcduffy-bottom-nav__badge');
      const cartIcon = document.querySelector('.mcduffy-bottom-nav__cart-icon');

      if (!cartIcon) return;

      if (count > 0) {
        if (badge) {
          badge.textContent = count;
          badge.classList.add('pop');
          setTimeout(() => badge.classList.remove('pop'), 300);
        } else {
          const newBadge = document.createElement('span');
          newBadge.className = 'mcduffy-bottom-nav__badge pop';
          newBadge.setAttribute('data-cart-count', '');
          newBadge.textContent = count;
          cartIcon.appendChild(newBadge);
        }
      } else if (badge) {
        badge.remove();
      }
    };

    // Listen for cart updates
    document.addEventListener('cart:updated', (e) => {
      const count = e.detail?.cart?.item_count || e.detail?.count || 0;
      updateBottomNavBadge(count);
    });
  };

  // ==========================================================================
  // 17. VIEWPORT HEIGHT FIX FOR MOBILE BROWSERS
  // ==========================================================================

  const initViewportFix = () => {
    // Fix for mobile browsers where 100vh includes the address bar
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', () => {
      setTimeout(setVH, 100);
    });
  };

  // ==========================================================================
  // 18. KEYBOARD SCROLL INTO VIEW FOR INPUTS
  // ==========================================================================

  const initKeyboardScroll = () => {
    if (window.innerWidth > 768) return;

    const inputs = document.querySelectorAll('input, textarea, select');

    inputs.forEach(input => {
      input.addEventListener('focus', () => {
        setTimeout(() => {
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      });
    });
  };

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  const init = () => {
    // Wait for DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAll);
    } else {
      initAll();
    }
  };

  const initAll = () => {
    initScrollAnimations();
    initCartBadgeAnimation();
    initSwipeGestures();
    initButtonRipple();
    initInputAnimations();
    initFormValidation();
    initCartAnimations();
    initBottomNav();
    initProductCards();
    initLazyLoading();
    initSmoothScroll();

    // Mobile-specific enhancements
    if (window.innerWidth <= 768) {
      initPullToClose();
      initCarouselSwipe();
      initKeyboardScroll();
    }

    // Always init these
    initLoadingStates();
    initBottomNavCartSync();
    initViewportFix();

    // Log init complete
    /* init complete */
  };

  // Start
  init();

})();
